import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Box,
  CircularProgress,
  TextField,
  Chip,
  Typography,
  Dialog,
  DialogContent,
  Button,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Checkbox,
  Alert,
  Snackbar,
} from "@mui/material";
import AppDataGrid from "../../../common/AppDataGrid";
import ModalDialogTitle from "../../../common/ModalDialogTitle";
import ConfirmDeleteDialog from "../../../common/ConfirmDeleteDialog";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  collection,
  query,
  orderBy,
  getDocs,
  getDoc,
  where,
  doc,
  deleteDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "../../../../config/firebase";
import dayjs from "dayjs";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import AdminCoursePlanningForm from "./AdminCoursePlanningForm";
import useSchoolDropdown from "../../../SchoolUsers/hooks/useSchoolDropdown";
import { Link } from "react-router-dom";
import CcpCpBreadcrumbs from "../../../Layout/inc/CcpCpBreadcrumbs";
import useExportXCSI from "../../../../hooks/useExportXCSI";
import { formatDate, formatDateTime } from "../../../../utils/formatDate";

// XCSI's own Drop_Grade_Req_Dt is unreliable for these exports — the
// school's actual drop-for-grade deadline is always the course's start
// date plus 14 days, so compute it directly instead of trusting that field.
const computeDropGradeDate = (startDt) => {
  if (!startDt) return "";
  const d = dayjs(startDt);
  return d.isValid() ? formatDate(d.add(14, "day").toDate()) : "";
};

const CoursePlanningLogViewer = ({
  schoolId = null,
  compact = false,
  maxResults = null,
  showSchoolForm = false,
  excludeUserColumn = false, // New prop to control User column visibility
  hideDate, // New prop for hide date value
  saving, // New prop for spinner
  onHideDateChange, // New prop for handler
}) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");
  const [schoolFilter, setSchoolFilter] = useState("All");
  const [specificDate, setSpecificDate] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedSchoolId, setSelectedSchoolId] = useState("");
  const [selectedSchoolName, setSelectedSchoolName] = useState("");
  const [dialogTab, setDialogTab] = useState(0);
  const [selectedLogIds, setSelectedLogIds] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    logsToDelete: [],
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [descriptionDialogOpen, setDescriptionDialogOpen] = useState(false);
  const [selectedDescription, setSelectedDescription] = useState("");
  const [selectedDescriptionLog, setSelectedDescriptionLog] = useState(null);
  const { exportXCSI, axiosLoading: exportAxiosLoading } = useExportXCSI();
  const [exportingXCSI, setExportingXCSI] = useState(false);

  // Get schools data for dropdown
  const { data: schools, loading: schoolsLoading } = useSchoolDropdown();

  // Helper function to get color rgba values
  const getColorRgba = (colorName) => {
    const colorMap = {
      primary: "rgba(25, 118, 210, ",
      success: "rgba(76, 175, 80, ",
      warning: "rgba(255, 152, 0, ",
      error: "rgba(244, 67, 54, ",
      info: "rgba(33, 150, 243, ",
      secondary: "rgba(156, 39, 176, ",
    };
    return colorMap[colorName] || "rgba(0, 0, 0, ";
  };

  // Fetch logs from Firestore
  useEffect(() => {
    const fetchLogs = async () => {
      try {
        setLoading(true);
        let q;

        if (schoolId) {
          // If schoolId is provided, filter by school
          q = query(
            collection(db, "CoursePlanningLog"),
            where("schoolId", "==", schoolId),
            orderBy("timestamp", "desc"),
          );
        } else {
          // If no schoolId, get all logs
          q = query(
            collection(db, "CoursePlanningLog"),
            orderBy("timestamp", "desc"),
          );
        }

        const querySnapshot = await getDocs(q);
        const logsData = [];

        querySnapshot.forEach((doc) => {
          const data = doc.data();
          logsData.push({
            id: doc.id,
            ...data,
            formattedTimestamp: formatDateTime(data.timestamp),
          });
        });

        // If maxResults is specified, limit the results
        const finalLogsData = maxResults
          ? logsData.slice(0, maxResults)
          : logsData;
        setLogs(finalLogsData);
      } catch (err) {
        console.error("Error fetching course planning logs:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [schoolId]);

  // Handle school click to open dialog
  const handleSchoolClick = (schoolId, schoolName) => {
    console.log("Opening dialog for school:", schoolId, schoolName);
    setSelectedSchoolId(schoolId);
    setSelectedSchoolName(schoolName);
    setDialogOpen(true);
  };

  // Handle dialog close
  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedSchoolId("");
    setSelectedSchoolName("");
    setDialogTab(0);
  };

  // Handle form submission
  const handleFormSubmit = () => {
    // Form will handle its own submission
    handleDialogClose();
  };

  // Handle log selection
  const handleSelectLog = (logId, checked) => {
    if (checked) {
      setSelectedLogIds((prev) => [...prev, logId]);
    } else {
      setSelectedLogIds((prev) => prev.filter((id) => id !== logId));
    }
  };

  // Handle select all logs
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedLogIds(filteredLogs.map((log) => log.id));
    } else {
      setSelectedLogIds([]);
    }
  };

  // Handle opening delete confirmation dialog
  const handleDeleteLogs = () => {
    setDeleteDialog({
      open: true,
      logsToDelete: selectedLogIds
        .map((id) => filteredLogs.find((log) => log.id === id))
        .filter(Boolean),
    });
  };

  // Handle confirming deletion
  const handleConfirmDelete = async () => {
    try {
      const batch = writeBatch(db);

      deleteDialog.logsToDelete.forEach((log) => {
        const logRef = doc(db, "CoursePlanningLog", log.id);
        batch.delete(logRef);
      });

      await batch.commit();

      // Update local state
      setLogs((prev) => prev.filter((log) => !selectedLogIds.includes(log.id)));
      setSelectedLogIds([]);
      setDeleteDialog({ open: false, logsToDelete: [] });

      // Show success message
      setSnackbar({
        open: true,
        message: `Successfully deleted ${deleteDialog.logsToDelete.length} log(s)`,
        severity: "success",
      });
    } catch (error) {
      console.error("Error deleting logs:", error);
      setSnackbar({
        open: true,
        message: "Error deleting logs. Please try again.",
        severity: "error",
      });
    }
  };

  // Handle closing delete dialog
  const handleCloseDeleteDialog = () => {
    setDeleteDialog({ open: false, logsToDelete: [] });
  };

  // Handle closing snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Export additions to CSV (custom format for external system)
  const handleExportAdditions = async () => {
    try {
      // Filter for Addition type logs only from the current filtered results
      const additionLogs = filteredLogs.filter(
        (log) => log.type === "Addition",
      );
      if (additionLogs.length === 0) {
        setSnackbar({
          open: true,
          message: "No course additions found in current filter results",
          severity: "warning",
        });
        return;
      }

      // Fetch reference data for lookups
      const [termsSnap, hsTermsSnap, coursesSnap, modalitySnap, bldgSnap] =
        await Promise.all([
          getDocs(collection(db, "CcpTerm")),
          getDocs(collection(db, "CcpHsTerms")),
          getDocs(collection(db, "CcpCourses")),
          getDocs(collection(db, "CcpModality")),
          getDocs(collection(db, "CcpBuildingMgt")),
        ]);
      // HS Faculty additions store their term from CcpHsTerms (a separate
      // collection from the "Courses W Sinclair" CcpTerm terms), so both
      // need to be checked or HS rows never match and fall back to the
      // full term name instead of the shortname.
      const terms = [
        ...termsSnap.docs.map((d) => d.data()),
        ...hsTermsSnap.docs.map((d) => d.data()),
      ];
      const courses = coursesSnap.docs.map((d) => d.data());
      const modalities = modalitySnap.docs.map((d) => d.data());
      const buildings = bldgSnap.docs.map((d) => d.data());

      // Helper: get Term Shortname
      const getTermShortname = (termName) => {
        const t = terms.find((t) => t.TermName === termName);
        return t?.TermShortname || termName || "";
      };
      // Helper: get Course Number and Capacity
      const getCourseInfo = (courseName) => {
        const c = courses.find((c) => c.Courses === courseName);
        return c
          ? { Number: c.Number, Capacity: c.Capacity }
          : { Number: "", Capacity: "" };
      };
      // Helper: get Modality Location
      const getModalityLocation = (modality) => {
        const m = modalities.find((m) => m.modality === modality);
        return m ? m.location : "";
      };
      // Helper: get Building Bldg and PC (simple direct match)
      const getBuildingInfo = (schoolName) => {
        const b = buildings.find((b) => b.highschool === schoolName);
        return b ? { Bldg: b.bldg, PC: b.pc } : { Bldg: "", PC: "" };
      };
      // Helper: get Coordinator Name
      const getCoordinatorName = async (schoolName) => {
        // Look up CcpSchools for pathway user id
        const schoolQuery = query(
          collection(db, "CcpSchools"),
          where("school_name", "==", schoolName),
        );
        const schoolSnapshot = await getDocs(schoolQuery);
        if (!schoolSnapshot.empty) {
          const pathwayUserId = schoolSnapshot.docs[0].data().pathway;
          if (pathwayUserId) {
            const userDoc = await getDoc(doc(db, "users", pathwayUserId));
            if (userDoc.exists()) {
              return userDoc.data().name || "";
            }
          }
        }
        return "";
      };

      // Map logs to export format (await for coordinator lookups)
      const csvData = await Promise.all(
        additionLogs.map(async (log) => {
          const courseData = log.rowData || log.courseData || {};
          // Use High_School from courseData if available, else fallback to log.schoolName
          const highSchoolName = courseData.High_School || log.schoolName || "";
          const termShort = getTermShortname(courseData.term);
          const courseInfo = getCourseInfo(courseData.courseName);
          const modalityLoc = getModalityLocation(courseData.modality);
          const bldgInfo = getBuildingInfo(highSchoolName);
          // Days logic
          const daysEntry = (courseData.days || "").toUpperCase();
          const daysRow = daysColumns.map((col) =>
            daysEntry === col.toUpperCase() ? "x" : "",
          );
          // Printed Comments: School + modality
          const printedComments = [highSchoolName, courseData.modality]
            .filter(Boolean)
            .join(" ");
          // Extract subject from course name (e.g., ACC-1101 -> ACC)
          let subject = "";
          if (courseData.courseName) {
            const match = courseData.courseName.match(/^([A-Za-z]+)/);
            subject = match ? match[1].toUpperCase() : "";
          }
          // Location: show modalityLoc, but if empty, show 'OFF'
          const locationValue =
            modalityLoc && modalityLoc.trim() !== "" ? modalityLoc : "OFF";
          // Coordinator lookup
          const coordinatorName = await getCoordinatorName(highSchoolName);
          return [
            highSchoolName, // High School
            termShort, // Term (shortname)
            "", // Blank
            subject, // Subject (new logic)
            courseInfo.Number || "", // Number
            "", // Section NO
            "", // Short Title
            "", // Credit Hours
            "", // Hybrid?
            locationValue, // Location (new logic)
            bldgInfo.Bldg || "", // BLDG
            "", // Room
            formatDate(courseData.startDate), // St Date
            formatDate(courseData.endDate), // End Date
            ...daysRow, // Days columns
            courseData.startTime || "", // Start Time
            courseData.endTime || "", // End Time
            courseInfo.Capacity || "", // Capacity
            "CCP", // REGISTRATION RESTRICTION
            coordinatorName, // COORDINATOR
            printedComments, // Printed Comments
            courseData.hsInstructorName || "", // Instructor name
            courseData.proctorName || "", // Proctor name
            courseData.hsComments || "", // Notes
          ];
        }),
      );

      // Create CSV content
      const csvContent = [headers, ...csvData]
        .map((row) =>
          row
            .map((cell) => `"${(cell || "").toString().replace(/"/g, '""')}"`)
            .join(","),
        )
        .join("\n");

      // Create and download file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      const currentDate = dayjs().format("YYYY-MM-DD");
      const filename = `Course_Additions_Export_${currentDate}.csv`;
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setSnackbar({
        open: true,
        message: `Successfully exported ${additionLogs.length} course additions to ${filename}`,
        severity: "success",
      });
    } catch (error) {
      console.error("Error exporting additions:", error);
      setSnackbar({
        open: true,
        message: "Error exporting course additions. Please try again.",
        severity: "error",
      });
    }
  };

  // Export changes to CSV
  const handleExportChanges = () => {
    try {
      console.log("[ExportChanges] Starting export");
      console.log("[ExportChanges] Total logs:", logs.length);
      console.log("[ExportChanges] Filtered logs count:", filteredLogs.length);

      // If the user has the type filter set to Update, use the full logs list for safety
      const sourceLogs =
        typeFilter === "Update"
          ? logs
          : typeFilter === "All"
            ? filteredLogs
            : filteredLogs;

      console.log("[ExportChanges] Using sourceLogs count:", sourceLogs.length);

      // Filter for Update type logs (including legacy "Course Update") and
      // XCSI-based Change Request logs from the current filtered results
      const updateLogs = sourceLogs.filter((log) => {
        const type = (log.type || "").toLowerCase();
        const desc = (log.description || "").toLowerCase();

        const isUpdateType =
          type === "update" ||
          type === "course update" ||
          type === "course updated";

        const isChangeRequestType =
          type === "change" || type === "change request";

        const looksLikeUpdate =
          desc.includes("course updated") || desc.includes("updated course");

        return isUpdateType || isChangeRequestType || looksLikeUpdate;
      });

      console.log(
        "[ExportChanges] Matched updateLogs count:",
        updateLogs.length,
      );

      if (updateLogs.length === 0) {
        setSnackbar({
          open: true,
          message: "No course changes found in current filter results",
          severity: "warning",
        });
        return;
      }

      // CSV Headers for course changes
      const headers = [
        "Term",
        "Course Section",
        "Days",
        "Start Tm",
        "End Tm",
        "Status",
        "Cap",
        "Used",
        "Cred",
        "Bldg",
        "Room",
        "Location",
        "Print Comments",
        "Restrictions",
        "Instructor",
        "Instructor Type",
        "Start Date",
        "End Date",
        "Drop Grade Date",
        "Withdrawal Date",
        "Change Action",
        "Specific Change",
        "Date/Time Submitted",
      ];

      // Convert logs to CSV data
      const csvData = updateLogs.map((log) => {
        // Extract course data from log.rowData (current values)
        const courseData = log.rowData || {};

        return [
          courseData.Term_Id_Cd || "",
          courseData.Course_Section_Nm || "",
          courseData.Days || "",
          courseData.Start_Time || "",
          courseData.End_Time || "",
          courseData.Course_Section_Status_Cd || "",
          courseData.Capacity_Ct || "",
          courseData.Used || "",
          courseData.Minimum_Credit_Hour_Ct || "",
          courseData.Bldg || "",
          courseData.room || "",
          courseData.Location || "",
          courseData.Printed_Comments_Ds || "",
          courseData.Reg_Restriction_Cd || "",
          courseData.Instructor || "",
          courseData.position || "",
          formatDate(courseData.Start_Dt),
          formatDate(courseData.End_Dt),
          computeDropGradeDate(courseData.Start_Dt),
          formatDate(courseData.Withdrawal_Dt),
          log.changeAction || "",
          log.specificChange || "",
          formatDateTime(log.timestamp),
        ];
      });

      // Create CSV content
      const csvContent = [headers, ...csvData]
        .map((row) =>
          row
            .map((cell) => `"${(cell || "").toString().replace(/"/g, '""')}"`)
            .join(","),
        )
        .join("\n");

      // Create and download file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);

      // Generate filename with current date
      const currentDate = dayjs().format("YYYY-MM-DD");
      const filename = `Course_Changes_Report_${currentDate}.csv`;
      link.setAttribute("download", filename);

      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log(
        "[ExportChanges] CSV generated and download triggered:",
        filename,
      );

      // Show success message
      setSnackbar({
        open: true,
        message: `Successfully exported ${updateLogs.length} course changes to ${filename}`,
        severity: "success",
      });
    } catch (error) {
      console.error("Error exporting changes:", error);
      setSnackbar({
        open: true,
        message: "Error exporting course changes. Please try again.",
        severity: "error",
      });
    }
  };

  // Export canceled courses to CSV
  const handleExportCanceled = () => {
    try {
      // Filter for Cancel Course type logs only from the current filtered results
      const canceledLogs = filteredLogs.filter(
        (log) => log.type === "Cancel Course",
      );

      if (canceledLogs.length === 0) {
        setSnackbar({
          open: true,
          message: "No canceled courses found in current filter results",
          severity: "warning",
        });
        return;
      }

      // CSV Headers for canceled courses (same setup as Export Changes)
      const headers = [
        "Term",
        "Course Section",
        "Days",
        "Start Tm",
        "End Tm",
        "Status",
        "Cap",
        "Used",
        "Cred",
        "Bldg",
        "Room",
        "Location",
        "Print Comments",
        "Restrictions",
        "Instructor",
        "Instructor Type",
        "Start Date",
        "End Date",
        "Drop Grade Date",
        "Withdrawal Date",
        "Change Action",
        "Specific Change",
      ];

      // Convert logs to CSV data
      const csvData = canceledLogs.map((log) => {
        // Extract course data from log.rowData (current values)
        const courseData = log.rowData || {};

        return [
          courseData.Term_Id_Cd || "",
          courseData.Course_Section_Nm || "",
          courseData.Days || "",
          courseData.Start_Time || "",
          courseData.End_Time || "",
          courseData.Course_Section_Status_Cd || "",
          courseData.Capacity_Ct || "",
          courseData.Used || "",
          courseData.Minimum_Credit_Hour_Ct || "",
          courseData.Bldg || "",
          courseData.room || "",
          courseData.Location || "",
          courseData.Printed_Comments_Ds || "",
          courseData.Reg_Restriction_Cd || "",
          courseData.Instructor || "",
          courseData.position || "",
          formatDate(courseData.Start_Dt),
          formatDate(courseData.End_Dt),
          computeDropGradeDate(courseData.Start_Dt),
          formatDate(courseData.Withdrawal_Dt),
          log.changeAction || "",
          log.specificChange || "",
        ];
      });

      // Create CSV content
      const csvContent = [headers, ...csvData]
        .map((row) =>
          row
            .map((cell) => `"${(cell || "").toString().replace(/"/g, '""')}"`)
            .join(","),
        )
        .join("\n");

      // Create and download file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);

      // Generate filename with current date
      const currentDate = dayjs().format("YYYY-MM-DD");
      const filename = `Course_Canceled_Report_${currentDate}.csv`;
      link.setAttribute("download", filename);

      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Show success message
      setSnackbar({
        open: true,
        message: `Successfully exported ${canceledLogs.length} canceled courses to ${filename}`,
        severity: "success",
      });
    } catch (error) {
      console.error("Error exporting canceled courses:", error);
      setSnackbar({
        open: true,
        message: "Error exporting canceled courses. Please try again.",
        severity: "error",
      });
    }
  };

  // Helper to normalize log types for consistent filtering and display
  const normalizeLogType = (type) => {
    if (!type) return type;
    const t = type.trim().toLowerCase();
    if (t === "course update" || t === "update" || t === "course updated")
      return "Update";
    if (
      t === "course cancel requested" ||
      t === "cancel requested" ||
      t === "cancel request" ||
      t === "cancellation requested"
    )
      return "Course Cancel Requested";
    if (
      t === "course canceled complete" ||
      t === "cancel complete" ||
      t === "canceled complete" ||
      t === "cancellation complete"
    )
      return "Course Canceled Complete";
    return type;
  };

  // Calculate counts for each log type
  const logTypeCounts = logs.reduce((acc, log) => {
    // Normalize legacy/update/cancel types
    const normalizedType = normalizeLogType(log.type);
    acc[normalizedType] = (acc[normalizedType] || 0) + 1;
    acc.All = (acc.All || 0) + 1;
    return acc;
  }, {});

  // Define log types and their styling
  const logTypeConfig = {
    All: { color: "primary", label: "All" },
    Addition: { color: "primary", label: "Addition" },
    Update: { color: "warning", label: "Course Update" },
    Deletion: { color: "error", label: "Deletion" },
    Change: { color: "warning", label: "Change" },
    "Change Request": { color: "secondary", label: "Change Request" },
    "Course Cancel Requested": { color: "info", label: "Cancel Requested" },
    "Course Canceled Complete": {
      color: "secondary",
      label: "Cancel Complete",
    },
    "Cancel Course": { color: "error", label: "Cancel Course" },
  };

  // Filter logs based on search text, type, school, and date filters
  const filteredLogs = logs.filter((log) => {
    const normalizedType = normalizeLogType(log.type);
    const matchesSearch =
      searchText === "" ||
      log.description?.toLowerCase().includes(searchText.toLowerCase()) ||
      log.schoolName?.toLowerCase().includes(searchText.toLowerCase()) ||
      log.userEmail?.toLowerCase().includes(searchText.toLowerCase());

    const matchesType = typeFilter === "All" || normalizedType === typeFilter;

    const matchesSchool =
      schoolFilter === "All" || log.schoolName === schoolFilter;

    // Date filtering logic
    let matchesDate = true;
    if (specificDate) {
      // Filter by specific date
      const logDate = dayjs(log.timestamp);
      const targetDate = dayjs(specificDate);
      matchesDate = logDate.isSame(targetDate, "day");
    } else if (startDate && endDate) {
      // Filter by date range
      const logDate = dayjs(log.timestamp);
      const start = dayjs(startDate);
      const end = dayjs(endDate);
      matchesDate =
        logDate.isAfter(start.subtract(1, "day")) &&
        logDate.isBefore(end.add(1, "day"));
    } else if (startDate) {
      // Filter from start date onwards
      const logDate = dayjs(log.timestamp);
      const start = dayjs(startDate);
      matchesDate = logDate.isAfter(start.subtract(1, "day"));
    } else if (endDate) {
      // Filter up to end date
      const logDate = dayjs(log.timestamp);
      const end = dayjs(endDate);
      matchesDate = logDate.isBefore(end.add(1, "day"));
    }

    return matchesSearch && matchesType && matchesSchool && matchesDate;
  });

  // Handle description click to open dialog
  const handleDescriptionClick = (log) => {
    setSelectedDescription(log.description || "");
    setSelectedDescriptionLog(log || null);
    setDescriptionDialogOpen(true);
  };

  // Handle description dialog close
  const handleDescriptionDialogClose = () => {
    setDescriptionDialogOpen(false);
    setSelectedDescription("");
    setSelectedDescriptionLog(null);
  };

  // Helper to break a long description into individual change lines
  const getDescriptionLines = (description) => {
    if (!description) return [];

    return description
      .split(/\r?\n|,/)
      .map((seg) => seg.trim())
      .filter((seg) => seg.length > 0);
  };

  // Helper to get header text and body lines from description
  // For this version, we intentionally leave the header empty and
  // put the entire parsed description into the body.
  const getDescriptionHeaderAndBody = (description) => {
    const lines = getDescriptionLines(description);
    return { header: "", bodyLines: lines };
  };

  // Days columns
  const daysColumns = [
    "MWF",
    "TTH",
    "MTWTH",
    "MTWTHF",
    "MW",
    "M Only",
    "T Only",
    "W Only",
    "TH Only",
    "F Only",
  ];
  // CSV Headers in required order
  const headers = [
    "High School",
    "Term",
    "Blank",
    "Subject",
    "Number",
    "Section NO",
    "Short Title",
    "Credit Hours",
    "Hybrid?",
    "Location",
    "BLDG",
    "Room",
    "St Date",
    "End Date",
    ...daysColumns,
    "Start Time",
    "End Time",
    "Capacity",
    "REGISTRATION RESTRICTION",
    "COORDINATOR",
    "Printed Comments",
    // Add the three new headers at the end
    "Instructor name",
    "Proctor name",
    "Notes",
  ];

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading course planning logs...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Typography color="error">Error loading logs: {error}</Typography>
        </CardContent>
      </Card>
    );
  }

  // Render compact view for embedded display
  if (compact) {
    const recentLogs = logs.slice(0, maxResults || 10);

    if (recentLogs.length === 0) {
      return (
        <Box sx={{ p: 2, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            No recent activity
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ p: 1 }}>
        {recentLogs.map((log, index) => (
          <Box
            key={log.id}
            sx={{
              p: 1.5,
              borderBottom:
                index < recentLogs.length - 1 ? "1px solid #e0e0e0" : "none",
              "&:hover": { backgroundColor: "#f9f9f9" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 0.5,
              }}
            >
              <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                <Chip
                  label={log.type}
                  size="small"
                  color={
                    log.type === "Addition"
                      ? "primary"
                      : log.type === "Update"
                        ? "warning"
                        : log.type === "Deletion"
                          ? "error"
                          : log.type === "Change"
                            ? "warning"
                            : log.type === "Change Request"
                              ? "secondary"
                              : log.type === "Course Cancel Requested" ||
                                  log.type === "Course Canceled Complete"
                                ? "secondary"
                                : log.type === "Cancel Course"
                                  ? "error"
                                  : log.type === "Change Request Approved"
                                    ? "success"
                                    : log.type === "Change Request Rejected"
                                      ? "error"
                                      : log.type === "View"
                                        ? "info"
                                        : "default"
                  }
                  sx={{
                    fontSize: "0.75rem",
                    ...(log.type === "Change"
                      ? { backgroundColor: "#ed6c02", color: "white" }
                      : log.type === "Course Cancel Requested"
                        ? { backgroundColor: "#008080", color: "white" }
                        : log.type === "Course Canceled Complete"
                          ? { backgroundColor: "#800080", color: "white" }
                          : {}),
                  }}
                />
                {log.schoolName && (
                  <Chip
                    label={log.schoolName}
                    size="small"
                    variant="outlined"
                    sx={{ fontSize: "0.7rem", fontWeight: 500 }}
                  />
                )}
              </Box>
              <Typography variant="caption" color="text.secondary">
                {log.formattedTimestamp}
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
              {log.courseName || "Course entry"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {log.description}
            </Typography>
            {log.userName && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mt: 0.5 }}
              >
                by {log.userName}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
    );
  }

  // Columns for DataGridPro
  const columns = [
    {
      field: "schoolName",
      headerName: "School",
      width: 320, // Increased width to show full school name
      sortable: true,
    },
    {
      field: "type",
      headerName: "Type",
      width: 140,
      sortable: true,
      renderCell: (params) => {
        const typeConfig = {
          Addition: { color: "primary" },
          Update: { color: "warning" },
          Deletion: { color: "error" },
          Change: { color: "#ed6c02" }, // orange
          "Change Request": { color: "secondary" },
          "Course Cancel Requested": { color: "#008080" }, // teal
          "Course Canceled Complete": { color: "#800080" }, // purple
          "Cancel Course": { color: "error" },
          // Keep legacy colors for backward compatibility
          View: { color: "info" },
          "Change Request Approved": { color: "success" },
          "Change Request Rejected": { color: "error" },
        };
        const config = typeConfig[params.value];
        return (
          <Chip
            label={params.value}
            color={
              config &&
              typeof config.color === "string" &&
              !config.color.startsWith("#")
                ? config.color
                : "default"
            }
            size="small"
            sx={
              config && config.color.startsWith("#")
                ? { backgroundColor: config.color, color: "white" }
                : undefined
            }
          />
        );
      },
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      sortable: false,
      renderCell: (params) => (
        <Button
          variant="text"
          onClick={() => handleDescriptionClick(params.row)}
          sx={{
            textTransform: "none",
            justifyContent: "flex-start",
            padding: 0,
            minWidth: "auto",
            color: "#1976d2",
            "&:hover": {
              backgroundColor: "rgba(25, 118, 210, 0.04)",
            },
          }}
        >
          <Typography
            variant="body2"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100%",
            }}
          >
            {params.value}
          </Typography>
        </Button>
      ),
    },
    // Only show User column if NOT excluded
    ...(!excludeUserColumn
      ? [
          {
            field: "userEmail",
            headerName: "User",
            width: 180,
            sortable: true,
          },
        ]
      : []),
    // Move the select checkbox column to the end to avoid collision with the column menu
    {
      field: "select",
      headerName: "",
      width: 50,
      sortable: false,
      renderHeader: () => (
        <Checkbox
          indeterminate={
            selectedLogIds.length > 0 &&
            selectedLogIds.length < filteredLogs.length
          }
          checked={
            filteredLogs.length > 0 &&
            selectedLogIds.length === filteredLogs.length
          }
          onChange={handleSelectAll}
        />
      ),
      renderCell: (params) => (
        <Checkbox
          checked={selectedLogIds.includes(params.row.id)}
          onChange={(e) => handleSelectLog(params.row.id, e.target.checked)}
        />
      ),
    },
  ];

  return (
    <Box
      sx={{
        p: 3,
        minHeight: "100vh",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        width: "100%", // Make sure it expands full width
        maxWidth: "none", // Remove any maxWidth constraints
      }}
    >
      {/* Add margin above breadcrumbs for visual spacing */}
      <Box sx={{ mt: 5 }} />
      <CcpCpBreadcrumbs pageName="Course Planning Logs" />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Card
          sx={{
            flexGrow: 1,
            width: "100%",
            background: "#fff",
            borderRadius: 2,
            boxShadow: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "visible",
            minHeight: 600,
            maxWidth: "none", // Remove any maxWidth constraints
          }}
        >
          <CardContent>
            {/* Filters */}
            <Box sx={{ mb: 3 }}>
              {/* Filter Controls Grid */}
              <Grid container spacing={2} sx={{ mb: 2 }}>
                {/* School Filter */}
                {!schoolId && (
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Filter by School</InputLabel>
                      <Select
                        value={schoolFilter}
                        onChange={(e) => setSchoolFilter(e.target.value)}
                        label="Filter by School"
                      >
                        <MenuItem value="All">All Schools</MenuItem>
                        {schools.map((school) => (
                          <MenuItem
                            key={school.school_id}
                            value={school.school_name}
                          >
                            {school.school_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}

                {/* Term Filter (replaces date pickers if Courses w Sinclair is selected) */}
                {schoolFilter === "Courses w Sinclair" ? (
                  <>
                    <Grid item xs={12} sm={6} md={3}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Term</InputLabel>
                        <Select
                          value={typeFilter}
                          onChange={(e) => {
                            setTypeFilter(e.target.value);
                            // Example: populate start/end date based on term selection
                            if (e.target.value === "Spring 2026") {
                              setStartDate("2026-01-10");
                              setEndDate("2026-05-10");
                            } else if (e.target.value === "Fall 2026") {
                              setStartDate("2026-08-20");
                              setEndDate("2026-12-15");
                            } else {
                              setStartDate("");
                              setEndDate("");
                            }
                          }}
                          label="Term"
                        >
                          <MenuItem value="Spring 2026">Spring 2026</MenuItem>
                          <MenuItem value="Fall 2026">Fall 2026</MenuItem>
                          {/* Add more terms as needed */}
                        </Select>
                      </FormControl>
                    </Grid>
                    {/* Read-only Start Date (no calendar, no typing) */}
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        label="Start Date"
                        value={formatDate(startDate)}
                        fullWidth
                        size="small"
                        InputProps={{ readOnly: true, endAdornment: null }}
                        inputProps={{ readOnly: true }}
                        disabled
                      />
                    </Grid>
                    {/* Read-only End Date (no calendar, no typing) */}
                    <Grid item xs={12} sm={6} md={3}>
                      <TextField
                        label="End Date"
                        value={formatDate(endDate)}
                        fullWidth
                        size="small"
                        InputProps={{ readOnly: true, endAdornment: null }}
                        inputProps={{ readOnly: true }}
                        disabled
                      />
                    </Grid>
                  </>
                ) : (
                  <>
                    {/* Specific Date Filter */}
                    <Grid item xs={12} sm={6} md={3}>
                      <DatePicker
                        label="Filter by Date"
                        value={specificDate}
                        onChange={(newValue) => {
                          setSpecificDate(newValue);
                          // Clear date range when using specific date
                          if (newValue) {
                            setStartDate(null);
                            setEndDate(null);
                          }
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "small",
                          },
                        }}
                      />
                    </Grid>

                    {/* Start Date Filter */}
                    <Grid item xs={12} sm={6} md={3}>
                      <DatePicker
                        label="Start Date"
                        value={startDate}
                        onChange={(newValue) => {
                          setStartDate(newValue);
                          // Clear specific date when using date range
                          if (newValue) {
                            setSpecificDate(null);
                          }
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "small",
                            inputProps: { readOnly: true },
                          },
                        }}
                      />
                    </Grid>

                    {/* End Date Filter */}
                    <Grid item xs={12} sm={6} md={3}>
                      <DatePicker
                        label="End Date"
                        value={endDate}
                        onChange={(newValue) => {
                          setEndDate(newValue);
                          // Clear specific date when using date range
                          if (newValue) {
                            setSpecificDate(null);
                          }
                        }}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            size: "small",
                            inputProps: { readOnly: true },
                          },
                        }}
                      />
                    </Grid>
                  </>
                )}
              </Grid>

              {/* Search Field and Export Buttons */}
              <Box
                sx={{
                  mb: 2,
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                  flexWrap: "nowrap", // Prevent wrapping
                  width: "100%",
                }}
              >
                <TextField
                  label="Search logs"
                  variant="outlined"
                  size="small"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  sx={{ minWidth: 250, flexShrink: 0 }}
                />
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    flexWrap: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<DownloadIcon />}
                    onClick={handleExportAdditions}
                    size="small"
                    sx={{
                      minWidth: 150,
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                    }}
                  >
                    Export Additions
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    startIcon={<DownloadIcon />}
                    onClick={handleExportChanges}
                    size="small"
                    sx={{
                      minWidth: 150,
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                    }}
                  >
                    Export Changes
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<DownloadIcon />}
                    onClick={handleExportCanceled}
                    size="small"
                    sx={{
                      minWidth: 150,
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                    }}
                  >
                    Export Canceled
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<DownloadIcon />}
                    onClick={async () => {
                      setExportingXCSI(true);
                      try {
                        const res = await exportXCSI();
                        setSnackbar({
                          open: true,
                          message: `Exported ${res.rowsCount || 0} rows to Excel`,
                          severity: "success",
                        });
                      } catch (err) {
                        console.error("Export XCSI failed:", err);
                        setSnackbar({
                          open: true,
                          message:
                            "Export XCSI failed. See console for details.",
                          severity: "error",
                        });
                      } finally {
                        setExportingXCSI(false);
                      }
                    }}
                    size="small"
                    sx={{
                      minWidth: 150,
                      whiteSpace: "nowrap",
                      flexShrink: 0,
                      backgroundColor: "purple",
                      color: "#fff",
                      "&:hover": { backgroundColor: "darkviolet" },
                    }}
                  >
                    {exportingXCSI || exportAxiosLoading ? (
                      <CircularProgress size={16} sx={{ color: "#fff" }} />
                    ) : (
                      "Export XCSI"
                    )}
                  </Button>
                </Box>
                {/* Hide After Date field, now in export bar */}
                {typeof hideDate !== "undefined" &&
                  typeof onHideDateChange === "function" && (
                    <Box
                      sx={{ ml: 2, minWidth: 260, flexGrow: 1, maxWidth: 340 }}
                    >
                      <TextField
                        label="Course Planning Form - Hide After Date"
                        type="date"
                        name="coursePlanning"
                        value={hideDate || ""}
                        onChange={onHideDateChange}
                        InputLabelProps={{ shrink: true }}
                        helperText="Leave empty to keep form visible. Set date to hide form after this date."
                        sx={{ width: "100%" }}
                      />
                      {saving && (
                        <CircularProgress size={22} sx={{ ml: 1, mt: 1 }} />
                      )}
                    </Box>
                  )}
              </Box>

              {/* Status Filter Chips */}
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexWrap: "wrap",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mr: 1 }}
                >
                  Filter by type:
                </Typography>
                {Object.entries(logTypeConfig).map(([type, config]) => {
                  const count = logTypeCounts[type] || 0;
                  const isActive = typeFilter === type;

                  return (
                    <Chip
                      key={type}
                      label={`${config.label} (${count})`}
                      onClick={() => setTypeFilter(type)}
                      color={isActive ? config.color : "default"}
                      variant={isActive ? "filled" : "outlined"}
                      sx={{
                        cursor: "pointer",
                        opacity: count === 0 && type !== "All" ? 0.5 : 1,
                        backgroundColor:
                          !isActive && count > 0 && type !== "All"
                            ? `${getColorRgba(config.color)}0.1)`
                            : undefined,
                        "&:hover": {
                          backgroundColor: !isActive
                            ? count > 0
                              ? `${getColorRgba(config.color)}0.2)`
                              : "rgba(0, 0, 0, 0.04)"
                            : undefined,
                        },
                      }}
                    />
                  );
                })}
              </Box>

              <Typography variant="body2" color="text.secondary">
                Showing: {filteredLogs.length} of {logs.length} logs
              </Typography>

              {/* Delete Selected Button */}
              {selectedLogIds.length > 0 && (
                <Box
                  sx={{ mt: 2, display: "flex", alignItems: "center", gap: 2 }}
                >
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={handleDeleteLogs}
                    size="small"
                  >
                    Delete Selected ({selectedLogIds.length})
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => setSelectedLogIds([])}
                    size="small"
                  >
                    Clear Selection
                  </Button>
                </Box>
              )}
            </Box>

            {/* Data Grid */}
            <Box sx={{ height: 900, width: "100%", mb: "250px" }}>
              <AppDataGrid
                rows={filteredLogs}
                columns={columns}
                pageSize={filteredLogs.length || 10}
                rowsPerPageOptions={[filteredLogs.length || 10]}
                pagination
                disableSelectionOnClick
                disableColumnMenu
                sx={{
                  "& .MuiDataGrid-row:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                  },
                }}
              />
            </Box>
          </CardContent>
        </Card>
      </LocalizationProvider>

      {/* School Details Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleDialogClose}
        maxWidth="xl"
        fullWidth
      >
        <ModalDialogTitle onClose={handleDialogClose}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6">{selectedSchoolName}</Typography>
            <Chip
              label="Course Planning Management"
              color="primary"
              size="small"
            />
          </Box>
        </ModalDialogTitle>
        <DialogContent>
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
            <Tabs
              value={dialogTab}
              onChange={(e, newValue) => setDialogTab(newValue)}
            >
              <Tab label="Edit Course Planning" />
              <Tab label="Activity Logs" />
            </Tabs>
          </Box>

          {/* Tab Content */}
          {dialogTab === 0 && (
            <Box>
              <AdminCoursePlanningForm
                onSubmit={handleFormSubmit}
                onCancel={handleDialogClose}
                schoolId={selectedSchoolId}
              />
            </Box>
          )}

          {dialogTab === 1 && (
            <Box>
              <CoursePlanningLogViewer
                schoolId={selectedSchoolId}
                compact={false}
                maxResults={50}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <ConfirmDeleteDialog
        open={deleteDialog.open}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDelete}
        title="Confirm Deletion"
        message={
          <>
            Are you sure you want to delete the selected log(s)? This action
            cannot be undone.
            <Box sx={{ mt: 2 }}>
              {deleteDialog.logsToDelete.map((log) => (
                <Chip
                  key={log.id}
                  label={log.description}
                  size="small"
                  sx={{ mr: 1, mb: 1 }}
                />
              ))}
            </Box>
          </>
        }
      />
      <Dialog
        open={descriptionDialogOpen}
        onClose={handleDescriptionDialogClose}
        maxWidth="sm"
        fullWidth
      >
        {(() => {
          const log = selectedDescriptionLog;
          return (
            <>
              <ModalDialogTitle onClose={handleDescriptionDialogClose}>
                Change Details
              </ModalDialogTitle>
              <DialogContent dividers>
                {log?.changeAction && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: "bold",
                        color: "text.secondary",
                        fontSize: "0.75rem",
                        textTransform: "uppercase",
                        mb: 0.5,
                      }}
                    >
                      Change Action
                    </Typography>
                    <Typography variant="body1">{log.changeAction}</Typography>
                  </Box>
                )}
                {log?.description && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: "bold",
                        color: "text.secondary",
                        fontSize: "0.75rem",
                        textTransform: "uppercase",
                        mb: 0.5,
                      }}
                    >
                      Description
                    </Typography>
                    <Typography variant="body1">{log.description}</Typography>
                  </Box>
                )}
                {log?.specificChange && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: "bold",
                        color: "text.secondary",
                        fontSize: "0.75rem",
                        textTransform: "uppercase",
                        mb: 0.5,
                      }}
                    >
                      Specific Change
                    </Typography>
                    <Typography variant="body1">
                      {log.specificChange}
                    </Typography>
                  </Box>
                )}
                {/* Fallback: show raw description lines if none of the structured fields exist */}
                {!log?.changeAction &&
                  !log?.specificChange &&
                  getDescriptionLines(selectedDescription).map(
                    (line, index) => (
                      <Typography key={index} variant="body2" sx={{ mb: 1 }}>
                        {index + 1}. {line}
                      </Typography>
                    ),
                  )}
              </DialogContent>
            </>
          );
        })()}
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CoursePlanningLogViewer;
